import HeaderOuBannerDependendoDaInterpretacaoH1 from "layout/header-ou-banner-dependendo-da-interpretacao-h1/HeaderOuBannerDependendoDaInterpretacaoH1"
import { Box, Checkbox, CircularProgress, Collapse, DialogContent, DialogTitle, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material"
import CustomDialog from "./custom/CustomDialog"
import { useCallback, useEffect, useState } from "react";
import FinancasService from "pages/financas/financas.service";
import { FlowContaReceberBaixaDTO, FlowContaReceberBaixasContaFinanceiraDTO, FlowContaReceberExclusaoDTO } from "pages/financas/financas.model";
import useCatchApiError from "hooks/useCatchApiError";
import { ColumnData, isChecked, isEmpty, resolveWrapper } from "utils/util";
import InfiniteScrollVirtuoso, { HeaderInfiniteScrollVirtuoso, InfiniteScrollTableProps, RowInfiniteScrollVirtuosoProps } from "./infinitescrollvirtuoso/InfiniteScrollVirtuoso";

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { CustomButton } from "./roundedbutton/CustomButton";
import AtencaoIcon from "./Icons/AtencaoIcon";
import { useSnackbarCustom } from "hooks/useSnackCustom";
import { theme } from "theme";

export type TExcluirContaReceberProps = {
    onClose: () => void;
    uuid: string;
    selecionados: Array<string>,
    onSelectUUID: (uuids: Array<string>, uuid: string) => void;
    onExcluir: (selecionados: Array<string>) => void;
}

export enum TBaixaContaFinanceira {
    descricao = 'Descrição',
    parcela = 'Parcela',
    vencimento = 'Vencimento',
    status = 'Status',
    valorParcela = 'Valor da parcela',
    multa = 'Multa',
    juros = 'Juros',
    desconto = 'Desconto',
    totalPago = 'Total pago',
}

export enum TRecebimentoExclusaoCheque {
    descricao = 'Descrição',
    parcela = 'Parcela',
    vencimento = 'Vencimento',
    status = 'Status',
    valorParcela = 'Valor da parcela',
}

export enum TBaixaCompensacao {
    descricao = 'Descrição',
    parcela = 'Parcela',
    vencimento = 'Vencimento',
    status = 'Status',
    valorParcela = 'Valor da parcela',
    valorBaixado = "Valor da baixa",
    lancamento = "Lancamento",
    historico = "Histórico",
    valor = "Valor"
}

type FlowContaReceberBaixasContaFinanceiraDTOWithoutCheque = Omit<
    FlowContaReceberBaixasContaFinanceiraDTO,
    "recebimentoEmCheque"
>;

const initialState = {
    FlowContaReceberExclusaoDTO: null as FlowContaReceberExclusaoDTO | null,
    colapsadas: [] as Array<string>,
    loading: true as boolean,
    showModalConfirmacao: false as boolean,
    possuiComissaoPaga: false as boolean
}

const ExcluirContaReceber = (props: TExcluirContaReceberProps) => {
    const financasService = FinancasService()
    const [stateLocal, setStateLocal] = useState(initialState)
    const { throwErrorMessage } = useCatchApiError()
    const { addError } = useSnackbarCustom()

    useEffect(() => {
        listarExclusaoDTO()
        //eslint-disable-next-line
    }, [])

    const listarExclusaoDTO = useCallback(async () => {
        try {
            const { data } = await financasService.listarExclusao(props.uuid)
            setStateLocal(prevState => ({ ...prevState, FlowContaReceberExclusaoDTO: data, loading: false }))
        }
        catch (err) {
            setStateLocal(prevState => ({ ...prevState, loading: false }))
            throwErrorMessage(err ?? [])
        }
        //eslint-disable-next-line
    }, [])

    const onToggle = useCallback((colapsadas: Array<string>, uuid: string) => {
        const newColapsadas = colapsadas.includes(uuid)
            ? colapsadas.filter(e => e !== uuid)
            : [...colapsadas, uuid];

        setStateLocal(prevState => ({
            ...prevState,
            colapsadas: newColapsadas
        }))
    }, [])

    const validaApuracoes = useCallback(async () => {
        try {
            const { data } = await financasService.validaApuracoes(props.uuid)
            setStateLocal(prevState => ({ ...prevState, possuiComissaoPaga: data, showModalConfirmacao: true }))
        }
        catch { }
        //eslint-disable-next-line
    }, [])

    const onOpenModalConfirmacao = useCallback(() => {
        if (isEmpty(props.selecionados)) {
            return addError({ message: "Selecione ao menos uma baixa.", id: "selecione-uma-baixa-id", closeable: true })
        }
        validaApuracoes()
        //eslint-disable-next-line
    }, [props.selecionados])

    const closeModalConfirmacao = useCallback(() => {
        setStateLocal(prevState => ({
            ...prevState,
            possuiComissaoPaga: initialState.possuiComissaoPaga,
            showModalConfirmacao: initialState.showModalConfirmacao
        }))
    }, [])

    const columns: ColumnData<FlowContaReceberBaixaDTO & { __checkbox: unknown }>[] = [
        {
            id: "checkbox",
            key: "__checkbox",
            header: "",
            type: "checkBox",
            width: 5,
            convert: (item) => {
                return (
                    <TableCell sx={{
                        flexDirection: "row",
                        display: "flex",
                        padding: 0,
                        borderBottom: 'unset'
                    }
                    } >
                        <Checkbox
                            id={`item-${item.uuid}-input`}
                            sx={{
                                '&.MuiCheckbox-root.Mui-checked': {
                                    color: theme.palette.primary.main
                                }
                            }}
                            onClick={() => props.onSelectUUID(props.selecionados, item.uuid)}
                            checked={isChecked(props.selecionados, item.uuid)}
                            disabled={item.disabled}
                        />

                        <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => onToggle(stateLocal.colapsadas, item.uuid)}
                        >
                            {isChecked(stateLocal.colapsadas, item.uuid) ? <KeyboardArrowDownIcon /> : <KeyboardArrowRight />}
                        </IconButton>
                    </TableCell >
                )
            }
        },
        {
            id: "lancamento",
            key: "lancamento",
            header: "Lancamento",
            type: "string",
            width: 10,
            textAlign: "center"
        },
        {
            id: "conta_financeira",
            key: "contaFinanceira",
            header: "Conta financeira",
            type: "string",
            width: 25,
            textAlign: "left"
        },
        {
            id: "historico",
            key: "historico",
            header: "Histórico",
            type: "string",
            width: 25,
            textAlign: "left",
            convert: (item, { extraData, column }) => {
                return (
                    <Tooltip title={item.historico}>
                        <TableCell sx={{
                            border: 0,
                            padding: 0,
                            width: extraData.calcWidth(column.width),
                            textAlign: column.textAlign,
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "noWrap",
                            maxWidth: extraData.calcWidth(column.width)
                        }}>
                            {item.historico}
                        </TableCell>
                    </Tooltip>
                )
            }
        },
        {
            id: "valor",
            key: "valor",
            header: "Valor",
            type: "string",
            width: 10,
            textAlign: "center"
        },
    ];

    const columnsTwo: ColumnData<FlowContaReceberBaixaDTO & { __checkbox: unknown }>[] = [
        {
            id: "checkbox",
            key: "__checkbox",
            header: "",
            type: "checkBox",
            width: 5,
            convert: (item) => {
                return (
                    <TableCell sx={{
                        flexDirection: "row",
                        display: "flex",
                        padding: 0,
                        borderBottom: 'unset'
                    }} >
                        <Checkbox
                            id={`item-${item.uuid}-input`}
                            sx={{
                                '&.MuiCheckbox-root.Mui-checked': {
                                    color: theme.palette.primary.main
                                }
                            }}
                            onClick={() => props.onSelectUUID(props.selecionados, item.uuid)}
                            checked={isChecked(props.selecionados, item.uuid)}
                            disabled={item.disabled}
                        />

                        <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => onToggle(stateLocal.colapsadas, item.uuid)}
                        >
                            {isChecked(stateLocal.colapsadas, item.uuid) ? <KeyboardArrowDownIcon /> : <KeyboardArrowRight />}
                        </IconButton>
                    </TableCell >
                )
            }
        },
        {
            id: "lancamento",
            key: "baixaCompensacaoConta.lancamento",
            header: "Lançamento",
            type: "string",
            width: 10,
            textAlign: "left"

        },
        {
            id: "historico",
            key: "baixaCompensacaoConta.historico",
            header: "Histórico",
            type: "string",
            width: 60,
            textAlign: "left"
        },
        {
            id: "valor",
            key: "baixaCompensacaoConta.valor",
            header: "Valor",
            type: "string",
            width: 10,
            textAlign: "right"
        },
    ]

    const renderItemBaixaCompensacaoContas = useCallback((tableProps: InfiniteScrollTableProps & { data: FlowContaReceberBaixaDTO; index: number }) => {
        return (
            TableCustomRowTwo({ ...tableProps, columns: columnsTwo })
        )
        //eslint-disable-next-line
    }, [])

    const TableCustomRow = ({ calcWidth, columns, data, index }: RowInfiniteScrollVirtuosoProps<FlowContaReceberBaixaDTO, FlowContaReceberBaixaDTO & { __checkbox: unknown, }>) => {
        if (!data) return <></>
        return (
            <TableCell
                colSpan={999}
                style={{ padding: 0 }}
            >
                <Table>
                    <TableBody>
                        <TableRow>
                            {columns.map((col, indexCol) => {
                                return <TableCell
                                    key={col.key + index + indexCol + '-row'}
                                    sx={{
                                        height: '30px',
                                        minWidth: col.minWidth,
                                        maxWidth: col.maxWidth,
                                        width: calcWidth(col.width),
                                        textAlign: col.textAlign,
                                        fontSize: '14px',
                                        padding: 0,
                                        paddingLeft: "15px",
                                        color: theme.palette.common.black,
                                        paddingRight: col.textAlign === "right" ? "15px" : 0
                                    }}
                                >
                                    {(col.convert?.(data as any, { column: col, extraData: { calcWidth } })) ?? resolveWrapper(data, col.key)}
                                </TableCell>
                            })}
                        </TableRow>

                        <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                            <TableCell style={{ padding: 0 }} colSpan={999}>
                                <Collapse in={isChecked(stateLocal.colapsadas, data.uuid)} timeout="auto" unmountOnExit>
                                    <Table size="small" aria-label="purchases">
                                        <TableHead>
                                            <TableRow>
                                                {Object.values(TBaixaContaFinanceira).map(e =>
                                                    <TableCell>
                                                        {e}
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            <TableRow
                                                key={data.uuid + "-row"}
                                            >
                                                {Object.keys(TBaixaContaFinanceira).map((e) => {
                                                    const baixaContaFinanceiraWithoutCheque: FlowContaReceberBaixasContaFinanceiraDTOWithoutCheque =
                                                        data.baixaContaFinanceira as FlowContaReceberBaixasContaFinanceiraDTOWithoutCheque;
                                                    return (
                                                        <TableCell>
                                                            {baixaContaFinanceiraWithoutCheque[e as keyof typeof baixaContaFinanceiraWithoutCheque]}
                                                        </TableCell>
                                                    )
                                                })}
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                    {!!data.baixaContaFinanceira.recebimentoEmCheque &&
                                        <Table size="small" aria-label="purchases" sx={{
                                            border: 0,
                                            padding: 0,
                                            margin: 0,
                                        }}>
                                            <DialogTitle id="dialog-title" textAlign={'left'} color={theme.palette.primary.main} sx={{
                                                border: 0,
                                                padding: 0,
                                                margin: 0,
                                                marginLeft: "15px",
                                                fontSize: 16
                                            }}>
                                                Recebimentos em cheque
                                            </DialogTitle>

                                            <TableRow>
                                                {Object.values(TRecebimentoExclusaoCheque).map((e, i) => {
                                                    return (
                                                        <TableCell sx={{
                                                            border: 0,
                                                            paddingTop: "5px",
                                                            paddingBottom: 0,
                                                            textAlign: e === TRecebimentoExclusaoCheque.valorParcela ? "right" : "left"
                                                        }}>
                                                            {e}
                                                        </TableCell>
                                                    )
                                                })}
                                            </TableRow>

                                            <TableBody>
                                                {data.baixaContaFinanceira.recebimentoEmCheque!.map((item, index) => (
                                                    <TableRow key={index} >

                                                        {Object.keys(TRecebimentoExclusaoCheque).map((key) => (
                                                            <TableCell key={key} sx={{
                                                                border: 0,
                                                                paddingTop: "5px",
                                                                paddingBottom: 0,
                                                                textAlign: key === "valorParcela" ? "right" : "left"
                                                            }}>
                                                                {item[key as keyof typeof item]}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    }
                                </Collapse>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table >
            </TableCell >
        );
    }

    const TableCustomRowTwo = ({ calcWidth, columns, data, index }: RowInfiniteScrollVirtuosoProps<FlowContaReceberBaixaDTO, FlowContaReceberBaixaDTO & { __checkbox: unknown, }>) => {
        return (
            <TableCell
                colSpan={999}
                style={{ padding: 0 }}
            >
                <Table>
                    <TableBody>
                        <TableRow>
                            {columns.map((col, indexCol) => {
                                return <TableCell
                                    key={col.key + index + indexCol + '-row'}
                                    sx={{
                                        height: '30px',
                                        minWidth: col.minWidth,
                                        maxWidth: col.maxWidth,
                                        width: calcWidth(col.width),
                                        textAlign: col.textAlign,
                                        fontSize: '14px',
                                        padding: 0,
                                        paddingLeft: "15px",
                                        color: theme.palette.common.black,
                                        paddingRight: col.textAlign === "right" ? "15px" : 0
                                    }}
                                >

                                    {(col.convert?.(data as any, { column: col, extraData: { calcWidth } })) ?? resolveWrapper(data, col.key)}
                                </TableCell>
                            })}
                        </TableRow>

                        <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                            <TableCell style={{ padding: 0 }} colSpan={999}>
                                <Collapse in={isChecked(stateLocal.colapsadas, data.uuid)} timeout="auto" unmountOnExit>
                                    <Table size="small" aria-label="purchases">
                                        <TableHead>
                                            <TableRow>
                                                {Object.values(TBaixaCompensacao).map(e => {
                                                    return (
                                                        <TableCell>
                                                            {e}
                                                        </TableCell>
                                                    )
                                                })}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow
                                                key={data.uuid + "-row"}
                                            >
                                                {Object.keys(TBaixaCompensacao).map((e) => {
                                                    return (
                                                        <TableCell sx={{
                                                            maxWidth: "250px"
                                                        }}>
                                                            {data.baixaCompensacaoConta[e as keyof typeof data.baixaCompensacaoConta]}
                                                        </TableCell>
                                                    )
                                                })}

                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Collapse>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table >
            </TableCell >
        );
    }

    return (
        <CustomDialog
            open
            fullWidth
            maxWidth={'xl'}
            sx={{
                maxWidth: "90vw"
            }}
            onClose={props.onClose}
        >
            <DialogTitle id="dialog-title" textAlign={'center'}>
                <HeaderOuBannerDependendoDaInterpretacaoH1
                    id='baixar-conta-receber'
                    iconClassName='fa-solid fa-sack-dollar'
                    label='Excluir recebimento'
                />
            </DialogTitle>

            <Box sx={{
                flexDirection: "row",
                display: "flex",
                justifyContent: "space-evenly",
                marginBottom: "20px",
            }}>
                <Box>
                    <Typography sx={{
                        textAlign: "center"
                    }}>
                        Descrição
                    </Typography>
                    <Typography sx={{
                        textAlign: "center"
                    }}>
                        {stateLocal.FlowContaReceberExclusaoDTO?.descricao}
                    </Typography>
                </Box>

                <Box>
                    <Typography sx={{
                        textAlign: "center"
                    }}>
                        Parcela
                    </Typography>
                    <Typography sx={{
                        textAlign: "center"
                    }}>
                        {stateLocal.FlowContaReceberExclusaoDTO?.parcela}
                    </Typography>
                </Box>

                <Box>
                    <Typography sx={{
                        textAlign: "center"
                    }}>
                        Vencimento
                    </Typography>
                    <Typography sx={{
                        textAlign: "center"
                    }}>
                        {stateLocal.FlowContaReceberExclusaoDTO?.vencimento}
                    </Typography>
                </Box>

                <Box>
                    <Typography sx={{
                        textAlign: "center"
                    }}>
                        Valor
                    </Typography>
                    <Typography sx={{
                        textAlign: "center"
                    }}>
                        {stateLocal.FlowContaReceberExclusaoDTO?.valor}
                    </Typography>
                </Box>
            </Box>

            <DialogContent sx={{
                overflowX: 'hidden'
            }}>
                <>
                    {!isEmpty(stateLocal.FlowContaReceberExclusaoDTO?.baixas.filter(e => !e.cheque)) &&
                        <>
                            <DialogTitle id="dialog-title" textAlign={'left'} color={theme.palette.primary.main}>
                                Baixas relacionadas a conta(s) financeira(s)
                            </DialogTitle>

                            <InfiniteScrollVirtuoso
                                data={stateLocal.FlowContaReceberExclusaoDTO?.baixas.filter(e => e.baixaContaFinanceira && !e.cheque) ?? []}
                                heightScale={"300px"}
                                fixedHeaderContent={(tableProps) => <HeaderInfiniteScrollVirtuoso {...tableProps} columns={columns} />}
                                renderItem={(tableProps) => TableCustomRow({ ...tableProps, columns })}
                            />
                        </>
                    }

                    {!isEmpty(stateLocal.FlowContaReceberExclusaoDTO?.baixas.filter(e => e.baixaCompensacaoConta)) &&
                        <>
                            <DialogTitle id="dialog-title" textAlign={'left'} color={theme.palette.primary.main}>
                                Baixas relacionadas a compensação entre contas
                            </DialogTitle>

                            <InfiniteScrollVirtuoso
                                data={stateLocal.FlowContaReceberExclusaoDTO?.baixas.filter(e => e.baixaCompensacaoConta) ?? []}
                                heightScale={"300px"}
                                fixedHeaderContent={(tableProps) => <HeaderInfiniteScrollVirtuoso {...tableProps} columns={columnsTwo} />}
                                renderItem={renderItemBaixaCompensacaoContas}
                            />
                        </>
                    }
                    {!isEmpty(stateLocal.FlowContaReceberExclusaoDTO?.baixas.filter(e => e.cheque)) &&
                        <>
                            <DialogTitle id="dialog-title" textAlign={'left'} color={theme.palette.primary.main}>
                                Baixas relacionadas a recebimentos em cheque
                            </DialogTitle>

                            <InfiniteScrollVirtuoso
                                data={stateLocal.FlowContaReceberExclusaoDTO?.baixas.filter(e => e.cheque) ?? []}
                                heightScale={"300px"}
                                fixedHeaderContent={(tableProps) => <HeaderInfiniteScrollVirtuoso {...tableProps} columns={columns} />}
                                renderItem={(tableProps) => TableCustomRow({ ...tableProps, columns })}
                            />
                        </>
                    }

                    <Box display={'flex'} justifyContent={'space-between'} marginTop={'15px'}>
                        <CustomButton
                            disabled={false}
                            onClick={props.onClose}
                            variant="contained"
                            color="error"
                        >
                            Voltar
                        </CustomButton>

                        <CustomButton
                            disabled={false}
                            onClick={onOpenModalConfirmacao}
                            variant="contained"
                            color="success"
                        >
                            Excluir
                        </CustomButton>
                    </Box>

                </>
            </DialogContent>
            {stateLocal.loading && <CircularProgress
                size={48}
                color='primary'
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-12px',
                }}
            />}
            {stateLocal.showModalConfirmacao &&
                <CustomDialog open
                    fullWidth
                    maxWidth={'sm'}
                    sx={{
                        maxWidth: "90vw"
                    }}
                    onClose={closeModalConfirmacao}>
                    <DialogTitle id="dialog-title" textAlign={'left'} sx={{
                        border: 0,
                        padding: 0,
                        margin: 0,
                        marginLeft: "15px",
                        marginBottom: "5px",
                        fontWeight: "bold"
                    }}>
                        Confirmação
                    </DialogTitle>
                    <Box sx={{
                        display: "flex",
                        flexDirection: "row",
                        borderBottom: "1px solid black",
                        alignItems: "center",
                        padding: "5px"
                    }}>
                        <AtencaoIcon fontSize="24px" />
                        <Typography sx={{
                            marginLeft: "10px"
                        }}>
                            Todas as baixas das demais contas relacionadas também serão excluídas! Deseja realmente excluir a(s) baixa(s)?
                        </Typography>
                    </Box>

                    {stateLocal.possuiComissaoPaga &&
                        <Box sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            bgcolor: theme.palette.warning.main,
                            marginTop: "10px",
                            padding: "5px"
                        }}>
                            <AtencaoIcon fontSize="24px" />
                            <Typography sx={{
                                marginLeft: "10px",
                                color: theme.palette.background.default
                            }}>
                                Comissão do representante já apurada, a exclusão irá reapurar a mesma. Deseja continuar?
                            </Typography>
                        </Box>
                    }
                    <Box display={'flex'} justifyContent={'space-between'} marginTop={'15px'}>
                        <CustomButton
                            disabled={false}
                            onClick={props.onClose}
                            variant="contained"
                            color="error"
                        >
                            Nao
                        </CustomButton>

                        <CustomButton
                            disabled={false}
                            onClick={() => props.onExcluir(props.selecionados)}
                            variant="contained"
                            color="success"
                        >
                            Sim
                        </CustomButton>
                    </Box>
                </CustomDialog>
            }
        </CustomDialog>
    )
}

export default ExcluirContaReceber
